import React, { createContext, useContext, useReducer } from 'react';
import type { ReactNode } from 'react';

export const CCP_SCREENS = {
    CHECKLIST_INFO: "CHECKLIST_INFO",
    CCP_OPRP_CHECKLIST: "CCP_OPRP_CHECKLIST",
    SIEVES_MAGNETS_CHECKLIST: "SIEVES_MAGNETS_CHECKLIST",
    SUMMARY: "SUMMARY"
} as const;

export type CCP_SCREENS = typeof CCP_SCREENS[keyof typeof CCP_SCREENS];

export interface CCPSessionData {
    site: string;
    line: string;
    shift: string;
    qaExecutive: string;
    productionIncharge: string;
    productVariety: string;
    parameterVerification: "CCP & OPRP" | "Sieves and Magnets";
    frequency: "4hrs" | "8hrs";
    cycle: string;
    tourStartDate: string;
    batchNo?: string;
}

export interface CCPOPRPCheckpointRow {
    checkpointName: string;
    description?: string;
    category: "OPRP" | "CCP";
    criteria: "Okay" | "Not Okay" | "";
    mdSensitivityValue?: string;
    defectRemarks?: string;
    actionTaken?: string;
    notifiedDepartment?: string;
    deviationStatus?: "None" | "New" | "Action Taken" | "Closed" | "Escalated";
    escalated?: "Yes" | "No";
    isMDSensitivity?: boolean;
    dbId?: string;
    tabName?: string;
    rowNumber?: number;
    subCheckName?: string;
    groupTitle?: string;
}

export interface SievesMagnetsItemRow {
    id: string;
    description: string;
    frequency: "4hrs" | "8hrs";
    plantGroup: "New Plant" | "Old Plant";
    criteria: "Okay" | "Not Okay" | "";
    defectRemarks?: string;
    actionTaken?: string;
    deviationStatus?: "None" | "New" | "Action Taken" | "Closed" | "Escalated";
    dbId?: string;
}

export interface CompletedCycleRecord {
    cycle: string;
    cycleDate: string;
    sessionTime?: string;
    productName?: string;
    executiveName?: string;
    location?: string;
    acceptanceResponse?: "OK" | "Line Not Operational" | "Plant Not Operational";
    acceptanceReason?: string;
    checkpoints?: CCPOPRPCheckpointRow[];
    sievesItems?: SievesMagnetsItemRow[];
    score?: number;
    hasDeviations?: boolean;
    photoFileNames?: string[];
    status?: "Metadata Setup" | "Checklist Filling" | "Checklist Filling - Paused" | "Completed" | "Pending Production Action" | "Pending QA Re-Verification" | "Escalated" | "Not Operational" | "Submitted";
    isPaused?: boolean;
}

interface CCPState {
    currentScreen: CCP_SCREENS;
    parameterVerification: "CCP & OPRP" | "Sieves and Magnets";
    selectedShift: string;
    selectedFrequency: "4hrs" | "8hrs";
    currentTourId: string | null;
    sessionData: CCPSessionData | null;
    currentCycle: string;
    completedCycles: CompletedCycleRecord[];
    isLoading: boolean;
    isPaused: boolean;
    canEditChecklist: boolean;
    canEditCorrectiveAction: boolean;
}

type CCPAction =
    | { type: 'SET_SCREEN'; payload: CCP_SCREENS }
    | { type: 'SET_PARAMETER_VERIFICATION'; payload: "CCP & OPRP" | "Sieves and Magnets" }
    | { type: 'SET_SELECTIONS'; payload: { parameterVerification: "CCP & OPRP" | "Sieves and Magnets"; shift: string; frequency?: "4hrs" | "8hrs" } }
    | { type: 'SET_TOUR_ID'; payload: string }
    | { type: 'SET_SESSION_DATA'; payload: CCPSessionData }
    | { type: 'SET_CURRENT_CYCLE'; payload: string }
    | { type: 'SET_COMPLETED_CYCLES'; payload: CompletedCycleRecord[] }
    | { type: 'ADD_COMPLETED_CYCLE'; payload: CompletedCycleRecord }
    | { type: 'UPDATE_COMPLETED_CYCLE'; payload: { cycle: string; record: Partial<CompletedCycleRecord> } }
    | { type: 'SET_PERMISSIONS'; payload: { canEditChecklist: boolean; canEditCorrectiveAction: boolean } }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_IS_PAUSED'; payload: boolean }
    | { type: 'RESET_STATE' };

const initialState: CCPState = {
    currentScreen: CCP_SCREENS.CHECKLIST_INFO,
    parameterVerification: "CCP & OPRP",
    selectedShift: "Shift 1",
    selectedFrequency: "8hrs",
    currentTourId: null,
    sessionData: null,
    currentCycle: "Cycle-1",
    completedCycles: [],
    isLoading: false,
    isPaused: false,
    canEditChecklist: true,
    canEditCorrectiveAction: true
};

function ccpReducer(state: CCPState, action: CCPAction): CCPState {
    switch (action.type) {
        case 'SET_SCREEN':
            return { ...state, currentScreen: action.payload };
        case 'SET_PARAMETER_VERIFICATION':
            return { ...state, parameterVerification: action.payload };
        case 'SET_SELECTIONS':
            return {
                ...state,
                parameterVerification: action.payload.parameterVerification,
                selectedShift: action.payload.shift,
                selectedFrequency: action.payload.frequency || state.selectedFrequency
            };
        case 'SET_TOUR_ID':
            return { ...state, currentTourId: action.payload };
        case 'SET_SESSION_DATA':
            return { ...state, sessionData: action.payload };
        case 'SET_CURRENT_CYCLE':
            return { ...state, currentCycle: action.payload };
        case 'SET_COMPLETED_CYCLES':
            return { ...state, completedCycles: action.payload };
        case 'ADD_COMPLETED_CYCLE': {
            const filtered = state.completedCycles.filter(c => c.cycle !== action.payload.cycle);
            return { ...state, completedCycles: [...filtered, action.payload] };
        }
        case 'UPDATE_COMPLETED_CYCLE': {
            const updated = state.completedCycles.map(c => {
                if (c.cycle === action.payload.cycle) {
                    return { ...c, ...action.payload.record };
                }
                return c;
            });
            return { ...state, completedCycles: updated };
        }
        case 'SET_PERMISSIONS':
            return {
                ...state,
                canEditChecklist: action.payload.canEditChecklist,
                canEditCorrectiveAction: action.payload.canEditCorrectiveAction
            };
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_IS_PAUSED':
            return { ...state, isPaused: action.payload };
        case 'RESET_STATE':
            return { ...initialState };
        default:
            return state;
    }
}

interface CCPContextType {
    state: CCPState;
    dispatch: React.Dispatch<CCPAction>;
}

const CCPOPRPSievesContext = createContext<CCPContextType | undefined>(undefined);

export const CCPOPRPSievesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(ccpReducer, initialState);

    return (
        <CCPOPRPSievesContext.Provider value={{ state, dispatch }}>
            {children}
        </CCPOPRPSievesContext.Provider>
    );
};

export const useCCPOPRPSievesContext = () => {
    const context = useContext(CCPOPRPSievesContext);
    if (!context) {
        throw new Error('useCCPOPRPSievesContext must be used within a CCPOPRPSievesProvider');
    }
    return context;
};
